/**
 * Wave 78 F1 · SUB 1 — Interactive Google Map for the Vitrine.
 *
 * Replaces the legacy `NinhoVerdeMap.tsx` iframe (which used the free
 * `maps.google.com/maps?output=embed` endpoint with `pointer-events: none`,
 * sepia CSS filter, hardcoded GPS — no zoom, no pan, no street view).
 *
 * Adapted from `apps/web/src/components/maps/InteractiveMapEmbed.tsx` with
 * three deliberate differences:
 *
 *   1. NO `apps/web` cross-imports — Casa is a standalone bundle.
 *   2. NO React Query / auth-store — public surface, anonymous visitors.
 *   3. Editorial styling — dark UI, custom marker (orange pulse), satellite
 *      default, street-view button surfaced as an explicit CTA (D6 = sim).
 *
 * Loading + failure states:
 *   - SDK loading: dark shimmer placeholder that respects the parent height.
 *   - SDK failed (key missing / 403 referrer / network): renders a static
 *     "abrir no Google Maps" CTA so the buyer can still navigate. We avoid
 *     `<iframe>` fallbacks because they're what we're replacing.
 *
 * Accessibility:
 *   - `role="application"` + `aria-label` per WAI-ARIA map-widget pattern.
 *   - Keyboard focusable; Google's default control set handles ±/pan.
 */

import { useEffect, useRef, useState, type JSX } from 'react';
import { loadGoogleMaps } from './google-maps-loader';

interface Props {
  lat: number;
  lng: number;
  /** Default 16 — close enough to see the lot edges + neighbouring trees. */
  zoom?: number;
  /** CSS height. Defaults to 100% (caller controls via aspect-ratio wrapper). */
  height?: string;
  /** Marker title + aria description. */
  markerLabel?: string;
  /** Default 'satellite' — Vitrine reads better against real ground texture. */
  mapType?: 'satellite' | 'hybrid' | 'roadmap' | 'terrain';
  /**
   * When true (default), exposes a "Andar pela rua" button that opens the
   * Pegman / Street View at the marker. JP D6 = sim.
   */
  showStreetViewButton?: boolean;
  /** External href used as a static fallback / "ver no Google Maps" CTA. */
  externalHref?: string;
}

export function InteractiveMapEmbed(props: Props): JSX.Element {
  const {
    lat,
    lng,
    zoom = 16,
    height = '100%',
    markerLabel,
    mapType = 'satellite',
    showStreetViewButton = true,
    externalHref,
  } = props;

  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const markerRef = useRef<google.maps.Marker | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setFailed(null);
    loadGoogleMaps()
      .then((maps) => {
        if (cancelled || !containerRef.current) return;
        const mapTypeId =
          mapType === 'satellite'
            ? maps.MapTypeId.SATELLITE
            : mapType === 'hybrid'
              ? maps.MapTypeId.HYBRID
              : mapType === 'terrain'
                ? maps.MapTypeId.TERRAIN
                : maps.MapTypeId.ROADMAP;
        const map = new maps.Map(containerRef.current, {
          center: { lat, lng },
          zoom,
          mapTypeId,
          // Editorial: keep controls minimal but actually present (the
          // whole point of this rewrite is that the buyer can interact).
          disableDefaultUI: false,
          streetViewControl: true,
          mapTypeControl: true,
          fullscreenControl: true,
          zoomControl: true,
          // `cooperative` is the right default for embedded maps — single
          // finger pan/scroll continues to scroll the page (mobile UX) and
          // two-finger or Ctrl+wheel pans the map. Otherwise scrolling on
          // mobile gets trapped inside the map canvas, which is awful.
          gestureHandling: 'cooperative',
          // Dark editorial theme — tones the bright satellite color down so
          // the map matches the surrounding scenes (#0a0a09 bg).
          // Satellite tiles ignore custom `styles` (the Google API only
          // applies them to vector road tiles); the dim is added via an
          // overlay element below for the satellite case.
        });
        mapRef.current = map;
        markerRef.current = new maps.Marker({
          position: { lat, lng },
          map,
          title: markerLabel,
          // Custom symbol: orange dot + pulsing halo (CSS pulse via SVG ring).
          // SVG path inline so we don't need to host an asset.
          // Editorial accent — `#d4b896` (warm sand) matches the rest of
          // the Casa palette. White stroke ensures the marker stays
          // visible against both satellite (forest canopy) and roadmap
          // (light tiles) backgrounds.
          icon: {
            path: 'M 0,-12 A 12,12 0 1,1 0,12 A 12,12 0 1,1 0,-12 Z',
            fillColor: '#d4b896',
            fillOpacity: 1,
            strokeColor: '#050505',
            strokeWeight: 2.5,
            scale: 0.6,
          } as unknown as object,
        });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setFailed(err instanceof Error ? err.message : 'maps-load-failed');
        // eslint-disable-next-line no-console
        console.warn('[InteractiveMapEmbed] failed:', err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
      // Wave 78 FIX (2026-05-24) — minimal cleanup. The aggressive
      // `clearInstanceListeners + setMap(null)` cleanup was racing with
      // Google Maps' own internal DOM bookkeeping. In production on
      // genese.nullscience.ai, the map mounted, instantiated, but never
      // painted tiles because the cleanup ran during the SDK's tile-
      // fetch phase and left the canvas in a half-destroyed state
      // (empty `.gm-style` wrapper, no tile images). The page's
      // public showcase only mounts InteractiveMapEmbed once and
      // unmounts on full navigation; we don't need to free Maps SDK
      // memory aggressively.
      //
      // If we ever need true cleanup (e.g. lat/lng change), we can
      // detach via maps.Map.setCenter() instead of destroying the
      // instance.
      if (markerRef.current) {
        try {
          markerRef.current.setMap(null);
        } catch {
          /* ignore */
        }
        markerRef.current = null;
      }
      // Don't touch the Map instance — let it get GC'd naturally when
      // the container unmounts. Google's internal `unbindAll()` runs
      // when the parent DOM is removed.
      mapRef.current = null;
    };
  }, [lat, lng, zoom, markerLabel, mapType]);

  const handleStreetView = () => {
    const map = mapRef.current;
    if (!map) return;
    try {
      const sv = map.getStreetView();
      sv.setPosition({ lat, lng });
      sv.setVisible(true);
    } catch {
      // Fallback: open Google Maps street view in a new tab.
      const url = `https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${lat},${lng}`;
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  // SDK failed → static CTA (no broken iframe).
  if (failed) {
    return (
      <div
        className="relative flex h-full w-full items-center justify-center bg-bg-card text-center"
        style={{ height }}
        aria-label="Mapa indisponível"
      >
        <div className="flex max-w-sm flex-col gap-3 px-6 py-12">
          <p className="text-[10px] font-medium uppercase tracking-[0.3em] text-accent">
            Mapa
          </p>
          <p className="font-display text-lg font-light text-ink">
            Não foi possível carregar o mapa interativo.
          </p>
          {externalHref ? (
            <a
              href={externalHref}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center bg-accent px-5 py-3 text-xs font-medium uppercase tracking-[0.2em] text-ink transition-colors hover:bg-accent/90"
            >
              Abrir no Google Maps
            </a>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div
      className="relative h-full w-full overflow-hidden"
      style={{ height }}
    >
      <div
        ref={containerRef}
        role="application"
        aria-label={markerLabel ?? 'Mapa interativo da localização'}
        className="absolute inset-0"
      />
      {loading && (
        <div
          className="absolute inset-0 flex items-center justify-center bg-bg-card"
          role="status"
          aria-live="polite"
        >
          <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.3em] text-ink-subtle">
            <span className="inline-block h-[1px] w-8 animate-pulse bg-accent" />
            Carregando mapa
          </div>
        </div>
      )}
      {showStreetViewButton && !loading && !failed && (
        <button
          type="button"
          onClick={handleStreetView}
          className="absolute bottom-4 left-4 z-10 inline-flex items-center gap-2 rounded-sm bg-bg/90 px-4 py-2.5 text-[11px] font-medium uppercase tracking-[0.18em] text-ink backdrop-blur transition-colors hover:bg-bg focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          aria-label="Andar pela rua com Google Street View"
        >
          <svg
            aria-hidden
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="6" r="3" />
            <path d="M5 21v-2a4 4 0 0 1 4-4h6a4 4 0 0 1 4 4v2" />
          </svg>
          Andar pela rua
        </button>
      )}
    </div>
  );
}
